import {
  Box,
  FormControl,
  FormLabel,
  HStack,
  Stack,
  Switch,
  Text,
} from '@cardboard-ui/react';
import { t } from '@lingui/macro';
import { useNodeSecurity } from 'apps/TenantApp/hooks/useNodeSecurity';
import { useNodeSecurity_node$key } from 'apps/TenantApp/hooks/useNodeSecurity/__generated__/useNodeSecurity_node.graphql';
import React from 'react';
import { Layout, useActiveLayout } from 'utils/LayoutProvider';

interface NodeSecurityRulesProps {
  compact?: boolean;
  node: useNodeSecurity_node$key;
  syncedNodeIds?: string[];
}

export const NodeSecurityRules = ({
  node,
  compact,
  syncedNodeIds = [],
}: NodeSecurityRulesProps) => {
  const {
    supportsNodeSecurity,
    supportsDownloadPrevention,
    supportsWatermark,
    isDownloadPrevented,
    togglePreventDownload,
    isTogglePreventDownloadLoading,
    hasWatermark,
    toggleWatermark,
    isToggleWatermarkLoading,
    canUpdateSecurity,
  } = useNodeSecurity(node);
  const isMobile = useActiveLayout() !== Layout.DESKTOP;

  if (!supportsNodeSecurity) {
    return null;
  }

  const notAllowedText = t`You are not allowed to update this setting.`;
  const notSupportedYetText = t`This security mechanism is not (yet) supported for this type of content.`;

  const baseDownloadPreventionDescription = t`By enabling this option, you can prevent users from downloading this item.`;
  const preventDownloadDescription = canUpdateSecurity
    ? !supportsDownloadPrevention
      ? notSupportedYetText
      : baseDownloadPreventionDescription
    : notAllowedText;

  const baseWatermarkDescription = t`By enabling this option, a digital watermark will be added to the item which identifies the viewer and time of viewing.`;
  const watermarkDescription = canUpdateSecurity
    ? !supportsWatermark
      ? notSupportedYetText
      : baseWatermarkDescription
    : notAllowedText;

  const allowedToUseDownloadToggle =
    canUpdateSecurity && supportsDownloadPrevention;
  const allowedToUseWatermarkToggle = canUpdateSecurity && supportsWatermark;

  return (
    <Stack
      direction={compact && !isMobile ? 'row' : 'column'}
      spacing={compact && !isMobile ? 4 : undefined}
    >
      <Box>
        <FormControl as={HStack}>
          <FormLabel
            color={!allowedToUseDownloadToggle ? 'gray.500' : undefined}
            _dark={{
              color: allowedToUseDownloadToggle ? 'gray.100' : 'gray.400',
            }}
            fontSize={compact ? 'sm' : 'md'}
            cursor={!allowedToUseDownloadToggle ? 'not-allowed' : undefined}
          >
            {t`Prevent download`}
            <Switch
              px={2}
              isDisabled={!allowedToUseDownloadToggle}
              isReadOnly={isTogglePreventDownloadLoading}
              isChecked={isDownloadPrevented}
              onChange={() => togglePreventDownload(syncedNodeIds)}
              size={compact ? 'sm' : 'md'}
            />
          </FormLabel>
        </FormControl>
      </Box>

      {!compact && (
        <Text opacity={0.8} fontSize="sm">
          {preventDownloadDescription}
        </Text>
      )}

      <Box>
        <FormControl as={HStack}>
          <FormLabel
            color={!allowedToUseWatermarkToggle ? 'gray.500' : undefined}
            _dark={{
              color: allowedToUseWatermarkToggle ? 'gray.100' : 'gray.400',
            }}
            fontSize={compact ? 'sm' : 'md'}
            cursor={!allowedToUseWatermarkToggle ? 'not-allowed' : undefined}
          >
            {t`Add watermark`}
            <Switch
              px={2}
              isDisabled={!allowedToUseWatermarkToggle}
              isReadOnly={isToggleWatermarkLoading}
              isChecked={hasWatermark}
              onChange={() => toggleWatermark(syncedNodeIds)}
              size={compact ? 'sm' : 'md'}
            />
          </FormLabel>
        </FormControl>
      </Box>

      {!compact && (
        <Text opacity={0.8} fontSize="sm">
          {watermarkDescription}
        </Text>
      )}
    </Stack>
  );
};
